import { useCallback, useEffect, useRef } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	isImageCreateData,
	DYNAMIC_UI_CREATE_KEY,
	IMAGE_CREATE_KEY,
	getRemixMetadata,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { duplicateMauiApp } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/duplicate-maui-app';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type { MessageActionPayload } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useChatStoreActions } from '@atlassian/conversation-assistant/controllers/chat-store';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { EditorInsertContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import { useEditorRemixContext } from './context';
import {
	appendConversationContextToUrl,
	getRemixIdFromMessage,
	getRemixUrl,
	getMauiUrl,
	getStreamingWhiteboardEmbedUrl,
	getWhiteboardUrl,
	getWhiteboardCreateActionData,
	insertEmbedIntoPage,
	sendWhiteboardStreamCompleteCommand,
	needsReparenting,
	getReparentContentType,
	FINALIZABLE_CONTENT_ACTION_KEYS,
	fetchInfographicUrl,
	isPageLevelRemix,
} from './utils/message';
import { getMauiIdFromUrl } from './utils/remix-ids';

const isFinalizePermissionError = (error: Error): boolean =>
	(error as Error & { errorAttributes?: { statusCode?: number } }).errorAttributes?.statusCode ===
	403;

export function useOnComplete(): void {
	const {
		isViewMode,
		isUpdate,
		historyEntryForUpdate,
		dynamicUiSubtypeForUpdate,
		dynamicUiType,
		dynamicUiSubtype: dynamicUiSubtypeFromContext,
		dynamicUiSource,
		conversationId,
		interactionSource,
		aspectRatio,
		finalMessage,
		contentClassification,
		clearThinkingDecoration,
		replaceWhiteboardEmbed,
		setMauiEmbedFinalizeStatus,
		setThinkingComplete,
		finishRemixThinking,
		addHistoryEntry,
		addHistoryStackEntry,
		onRemixInsertSuccess,
		onFinalizeFailed,
		onVariantUpdateComplete,
		onSubtypeMismatch,
		smartCreationConfig = { siteId: '', product: '', userId: '' },
		smartCreationContext,
		resetContext,
	} = useEditorRemixContext();
	const experienceTracker = useAIMateExperienceTracker();
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const { resolveConversationAction } = useChatStoreActions();

	const { userId: clientId, siteId } = smartCreationConfig;
	const action = finalMessage?.content?.message?.message?.actions?.[0];
	const actionData = action?.data;
	const actionKey = action?.key;
	const remixId = getRemixIdFromMessage({ message: finalMessage });
	const agentMessageId = finalMessage?.content?.message?.message?.id;
	const mediaFileId = isImageCreateData(actionData) ? actionData.mediaFileId : undefined;
	const backendDynamicUiSubtype = (actionData as { dynamicUiSubtype?: string } | undefined)
		?.dynamicUiSubtype;
	// Flag a true override only: both subtypes present and different. Excludes the
	// "Pick for me" case (no user selection).
	const isSubtypeMismatch =
		!!dynamicUiSubtypeFromContext &&
		!!backendDynamicUiSubtype &&
		dynamicUiSubtypeFromContext !== backendDynamicUiSubtype;
	const isLiveDoc = smartCreationContext?.confluence?.content?.subType === 'live';
	const contentType = smartCreationContext?.confluence?.content?.type;
	const currentPageId = smartCreationContext?.confluence?.content?.id;
	const spaceId = smartCreationContext?.confluence?.space?.id;
	const spaceKey = smartCreationContext?.confluence?.space?.key;
	const remixUrl = getRemixUrl(remixId, actionKey, spaceId, spaceKey);
	const finalWhiteboardCreateActionData = getWhiteboardCreateActionData(finalMessage);
	// For non-Confluence products, `context.editor` provides a pre-formed containerAri
	// for the duplicate remix API call and an editorId for routing the insert command
	// to the correct editor instance.
	const smartCreationEditorContext = smartCreationContext?.editor;
	// The page/editor ID used to route EditorInsertContentCommand via the rovo content bridge.
	// Prefers the Confluence content ID; falls back to the editor UUID for non-Confluence products.
	const insertReceiverId = currentPageId ?? smartCreationEditorContext?.editorId;

	/**
	 * Calls the convo-ai action endpoint when `aifc_finalize_convo_ai` is on.
	 * `onError` re-throws so callers can detect failure — without it, the chat
	 * store's safe-action wrapper would silently swallow the error.
	 */
	const finalizeAction = useCallback(
		async ({
			contentId,
			parentId,
			resolvedSpaceId,
		}: {
			contentId: string;
			parentId: string | undefined;
			resolvedSpaceId: string | undefined;
		}): Promise<void> => {
			if (!conversationId) {
				return;
			}

			// Only finalize via convo-ai for Confluence object types (pages, whiteboards,
			// databases). MAUI apps and images don't need this endpoint.
			if (!actionKey || !FINALIZABLE_CONTENT_ACTION_KEYS.has(actionKey)) {
				return;
			}

			const message = finalMessage?.content?.message?.message;
			const messageAction = message?.actions?.[0];
			if (!message || !messageAction) {
				return;
			}

			const isActionComplete =
				finalMessage?.content.streamingComplete ||
				messageAction?.status?.toLowerCase() === 'planned' ||
				messageAction?.status?.toLowerCase() === 'finished';

			if (!isActionComplete) {
				return;
			}

			const existingData = (messageAction.data as Record<string, unknown>) ?? {};
			const existingMetadata = (existingData.metadata as Record<string, unknown>) ?? {};

			const payload: MessageActionPayload = {
				context: {
					key: messageAction.key,
					invocationId: messageAction.invocationId,
					actionId: messageAction.actionId,
					displayName: messageAction.displayName,
					status: messageAction.status,
					toolSource: messageAction.toolSource,
					humanMessageId: messageAction.humanMessageId,
					confirmationMessage: messageAction.confirmationMessage,
					executedData: messageAction.executedData,
					messageId: message.id,
					resolution: {
						type: 'confirm',
						message: 'finalize',
					},
					data: {
						...existingData,
						metadata: {
							...existingMetadata,
							contentId,
							parentId,
							spaceId: resolvedSpaceId,
						},
					},
				},
			};

			await resolveConversationAction({
				conversationId,
				messageId: message.id,
				payload,
				actionContentProcessor: (msg) => ({
					message: { message_metadata: msg?.message_metadata },
				}),
				onError: ({ error }) => {
					throw error;
				},
			});
		},
		[finalMessage, resolveConversationAction, conversationId, actionKey],
	);

	const clearThinkingDecorationRef = useRef(clearThinkingDecoration);
	useEffect(() => {
		clearThinkingDecorationRef.current = clearThinkingDecoration;
	}, [clearThinkingDecoration]);

	const replaceWhiteboardEmbedRef = useRef(replaceWhiteboardEmbed);
	useEffect(() => {
		replaceWhiteboardEmbedRef.current = replaceWhiteboardEmbed;
	}, [replaceWhiteboardEmbed]);

	const setMauiEmbedFinalizeStatusRef = useRef(setMauiEmbedFinalizeStatus);
	useEffect(() => {
		setMauiEmbedFinalizeStatusRef.current = setMauiEmbedFinalizeStatus;
	}, [setMauiEmbedFinalizeStatus]);

	// Captures the suppressed contentId so the abandoned-stream backstop can
	// release it when finalMessage (hence remixId) is null and no other id source exists.
	const streamingContentIdRef = useRef<string | undefined>(undefined);

	const setThinkingCompleteRef = useRef(setThinkingComplete);
	useEffect(() => {
		setThinkingCompleteRef.current = setThinkingComplete;
	}, [setThinkingComplete]);

	const finishRemixThinkingRef = useRef(finishRemixThinking);
	useEffect(() => {
		finishRemixThinkingRef.current = finishRemixThinking;
	}, [finishRemixThinking]);

	const hasInsertedRef = useRef(false);
	const hasEarlyInsertedWhiteboardRef = useRef(false);
	const earlyInsertedWhiteboardLocalIdRef = useRef<string | undefined>(undefined);
	const earlyInsertedWhiteboardUrlRef = useRef<string | undefined>(undefined);

	const insertEarlyStreamingWhiteboard = useCallback(
		(contentId: string) => {
			const earlyRemixUrl = getStreamingWhiteboardEmbedUrl(contentId);
			const localId = crypto.randomUUID();

			hasEarlyInsertedWhiteboardRef.current = true;
			earlyInsertedWhiteboardLocalIdRef.current = localId;
			earlyInsertedWhiteboardUrlRef.current = earlyRemixUrl;
			streamingContentIdRef.current = contentId;

			// Suppress before insert so the banner mounts with a known status instead of warning mid-stream.
			setMauiEmbedFinalizeStatusRef.current?.({ contentId, status: 'suppressed' });

			insertEmbedIntoPage({
				url: earlyRemixUrl,
				insertReceiverId,
				isUpdate,
				localId,
			});

			// The whiteboard iframe is now the streaming surface. Hide only the editor-owned
			// generating decoration; keep the Remix flow mounted so finalMessage can still
			// trigger finalize/reparent and history recording.
			clearThinkingDecorationRef.current?.();
		},
		[insertReceiverId, isUpdate],
	);

	const replaceEarlyStreamingWhiteboard = useCallback((contentId: string, finalizedUrl: string) => {
		replaceWhiteboardEmbedRef.current?.({
			contentId,
			finalizedUrl,
			localId: earlyInsertedWhiteboardLocalIdRef.current,
		});
	}, []);

	const handleInsertMauiEmbedIntoPage = useCallback(async () => {
		let url = '';

		if (!conversationId || !remixId || !remixUrl) {
			return;
		}

		experienceTracker.start({
			name: ExperienceName.REMIX_INSERT,
			id: ExperienceName.REMIX_INSERT,
			attributes: {
				actionKey,
				dynamicUiType,
				contentType: getReparentContentType(actionKey),
				conversationChannelId: conversationId,
			},
		});

		trackAIMAUEvent({
			type: 'actioned',
			actionResult: 'contentFinalized',
			source: 'AIMate',
			invokedFrom: 'remix',
			proactive: false,
			userGenerated: true,
			singleInstrumentationID: agentMessageId,
			pageContext: 'confluence-page-edit',
			attributes: {
				createdContentProduct: 'confluence',
				createdContentType: getReparentContentType(actionKey),
				createdContentMetadata: {
					remixId,
					contentId: currentPageId,
					spaceId,
					isLiveDoc,
				},
				actionKey,
				dynamicUiType,
				dynamicUiSubtype: dynamicUiSubtypeFromContext ?? backendDynamicUiSubtype,
				userAction: 'insert',
			},
		});

		try {
			// For MAUI apps, duplicate the app first, then insert the duplicated URL.
			// When context.editor is set (gated by platform_editor_maui_jira_updates), use its
			// pre-formed containerAri. Otherwise build a Confluence ARI from the page ID.
			if (actionKey === DYNAMIC_UI_CREATE_KEY && (currentPageId || smartCreationEditorContext)) {
				const duplicateExperienceAttributes = {
					appId: remixId,
					currentPageId,
					source: 'mauiEmbedFinalizeButtons',
				};
				experienceTracker.start({
					name: ExperienceName.REMIX_DUPLICATE_APP,
					id: ExperienceName.REMIX_DUPLICATE_APP,
					attributes: duplicateExperienceAttributes,
				});

				let duplicateFailed = false;
				try {
					const { id: newAppId } = await duplicateMauiApp(
						remixId,
						smartCreationEditorContext
							? { containerAri: smartCreationEditorContext.containerAri }
							: {
									containerContentId: currentPageId ?? '',
									cloudId: smartCreationConfig.siteId,
									contentType,
								},
					);
					const duplicatedUrl = getMauiUrl(newAppId);
					insertEmbedIntoPage({ url: duplicatedUrl, insertReceiverId, isUpdate });
					url = duplicatedUrl;

					sendAnalyticsEvent({
						eventType: 'operational',
						actionSubject: 'mauiAppDuplicate',
						action: 'succeeded',
						actionSubjectId: 'mauiEmbedInsertIntoPage',
						attributes: {
							originalId: remixId,
							newId: newAppId,
						},
					});

					experienceTracker.succeed({
						name: ExperienceName.REMIX_DUPLICATE_APP,
						attributes: { ...duplicateExperienceAttributes, newAppId },
					});
				} catch (error) {
					duplicateFailed = true;
					sendAnalyticsEvent({
						eventType: 'operational',
						actionSubject: 'mauiAppDuplicate',
						action: 'failed',
						actionSubjectId: 'mauiEmbedInsertIntoPage',
						attributes: {
							originalId: remixId,
						},
					});

					const duplicateError =
						error instanceof Error ? error : new Error('Failed to duplicate MAUI app');
					experienceTracker.fail({
						name: ExperienceName.REMIX_DUPLICATE_APP,
						error: duplicateError,
						attributes: duplicateExperienceAttributes,
					});
					experienceTracker.fail({
						name: ExperienceName.REMIX_INSERT,
						error: duplicateError,
						attributes: {
							...duplicateExperienceAttributes,
							failedDueTo: ExperienceName.REMIX_DUPLICATE_APP,
						},
					});

					// Duplication failed — insert the original URL as a fallback
					// so the user isn't left with nothing.
					insertEmbedIntoPage({ url: remixUrl, insertReceiverId, isUpdate });
					url = remixUrl;
				}

				if (!duplicateFailed) {
					experienceTracker.succeed({ name: ExperienceName.REMIX_INSERT });
				}
				return url;
			}

			if (!needsReparenting(actionKey)) {
				// Fallback for MAUI apps reached without a currentPageId (the
				// duplicate flow above requires one). Insert the original URL
				// as-is.
				insertEmbedIntoPage({ url: remixUrl, insertReceiverId, isUpdate });
				url = remixUrl;
				try {
					await finalizeAction({
						contentId: remixId,
						parentId: currentPageId,
						resolvedSpaceId: spaceId,
					});
				} catch (error) {
					experienceTracker.fail({
						name: ExperienceName.REMIX_INSERT,
						error: error instanceof Error ? error : new Error('finalizeAction failed'),
					});
					return;
				}
				experienceTracker.succeed({ name: ExperienceName.REMIX_INSERT });
				return url;
			}

			// Whiteboards must be durable before the page keeps a final embed URL. If we
			// inserted a temporary streaming URL on the first chunk, finalization still runs
			// here and then swaps that exact node to the canonical finalized URL by localId.
			//
			// Append `_rc` so the Confluence in-embed recovery picker can
			// finalize a stuck embed via the convo-ai endpoint if inline
			// finalize fails (e.g. permission error on current space).
			// Only when the killswitch is OFF; falls back to plain remixUrl.
			const finalizeAnywhereKillswitchOn =
				// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
				FeatureGates.getExperimentValue<boolean>(
					'maui-native-embed-finalize-diagram-anywhere',
					'value',
					false,
				);
			// Recovery URL used only when finalize fails. The board was NOT moved
			// server-side (it still lives in the placeholder Rovo space), so the
			// embed must point at a placeholder-space URL — NOT `remixUrl`, which
			// embeds the current space key and would make the unfinalized diagram
			// look as if it had been moved. We deliberately omit `spaceKey` here so
			// `getWhiteboardUrl` falls back to the `~{spaceId}`/`~` placeholder, and
			// append `_rc` so the in-embed recovery picker can finalize it later.
			// Only when the killswitch is OFF; otherwise the embed is left as-is.
			const placeholderWhiteboardUrl = getWhiteboardUrl(remixId, spaceId);
			const whiteboardUrlWithRc = !finalizeAnywhereKillswitchOn
				? appendConversationContextToUrl(placeholderWhiteboardUrl, {
						conversationId,
						messageId: agentMessageId,
						invocationId: action?.invocationId,
					})
				: placeholderWhiteboardUrl;

			let finalizeError: Error | undefined;
			try {
				await finalizeAction({
					contentId: remixId,
					parentId: currentPageId,
					resolvedSpaceId: spaceId,
				});
			} catch (error) {
				finalizeError = error instanceof Error ? error : new Error('Failed to finalize whiteboard');
			}

			if (!finalizeError) {
				// Finalize succeeded — insert/replace with plain URL (no `_rc` needed).
				if (hasEarlyInsertedWhiteboardRef.current) {
					// Success rewrites the URL → FINALIZED, keeping the banner hidden.
					replaceEarlyStreamingWhiteboard(remixId, remixUrl);
					streamingContentIdRef.current = undefined;
				} else {
					insertEmbedIntoPage({ url: remixUrl, insertReceiverId, isUpdate });
				}
				url = remixUrl;
				experienceTracker.succeed({ name: ExperienceName.REMIX_INSERT });
				return url;
			}

			if (hasEarlyInsertedWhiteboardRef.current) {
				// Finalize failed — swap streaming URL for the placeholder `_rc`
				// URL so the in-embed recovery picker can finalize into a writable
				// space, regardless of error type (permission or otherwise).
				// When the killswitch is ON we skip the swap and leave the stuck
				// streaming embed in place (no `_rc` recovery flow).
				if (!finalizeAnywhereKillswitchOn) {
					replaceEarlyStreamingWhiteboard(remixId, whiteboardUrlWithRc);
					earlyInsertedWhiteboardUrlRef.current = whiteboardUrlWithRc;
				}
				if (isFinalizePermissionError(finalizeError)) {
					experienceTracker.succeed({
						name: ExperienceName.REMIX_INSERT,
						attributes: { isFinalizePermissionError: true },
					});
				} else {
					experienceTracker.fail({
						name: ExperienceName.REMIX_INSERT,
						error: finalizeError,
					});
				}
			} else {
				experienceTracker.fail({
					name: ExperienceName.REMIX_INSERT,
					error: finalizeError,
				});
			}

			if (hasEarlyInsertedWhiteboardRef.current) {
				// The diagram remains inserted on the page. Hand the failure to the
				// product integration so it can open its recovery UX. For Confluence
				// this opens the shared MAUI embed space picker (the same UI used by
				// the paste / 2-minute needs-finalize banner flow), giving the user a
				// way to choose a space they can write to and finalize there.
				if (onFinalizeFailed) {
					// Hand the picker the URL that is actually on the embed node: the
					// placeholder-space URL carrying `_rc` (set above via
					// replaceEarlyStreamingWhiteboard). This lets the picker (1) parse
					// `_rc` and finalize via the convo-ai `/action` endpoint, and (2)
					// match the node in updateUrlInDoc so the success rewrite strips
					// `_rc` and swaps the space key. Passing `remixUrl` here would lack
					// `_rc` (forcing the monolith `/finalize` path) and would not match
					// the embed node.
					onFinalizeFailed({ contentId: remixId, originalUrl: whiteboardUrlWithRc });
					// The picker drives recovery from here; clear the streaming
					// suppression bookkeeping so the embed's banner state isn't left
					// stuck in a suppressed state.
					streamingContentIdRef.current = undefined;
				} else if (streamingContentIdRef.current) {
					// No product recovery UX wired — fall back to the needs-finalize
					// banner (embed is still in a Rovo system space).
					setMauiEmbedFinalizeStatusRef.current?.({
						contentId: streamingContentIdRef.current,
						status: 'needs-finalize',
					});
					streamingContentIdRef.current = undefined;
				}
				return earlyInsertedWhiteboardUrlRef.current;
			}
		} catch (error) {
			experienceTracker.fail({
				name: ExperienceName.REMIX_INSERT,
				error: error instanceof Error ? error : new Error('Failed to insert into page'),
			});
		}
	}, [
		action,
		conversationId,
		contentType,
		remixUrl,
		remixId,
		actionKey,
		onFinalizeFailed,
		isLiveDoc,
		spaceId,
		agentMessageId,
		smartCreationEditorContext,
		currentPageId,
		dynamicUiType,
		dynamicUiSubtypeFromContext,
		backendDynamicUiSubtype,
		insertReceiverId,
		finalizeAction,
		experienceTracker,
		trackAIMAUEvent,
		sendAnalyticsEvent,
		smartCreationConfig.siteId,
		isUpdate,
		replaceEarlyStreamingWhiteboard,
	]);

	const handleInsertInfographicIntoPage = useCallback(async () => {
		if (!mediaFileId || !clientId || !insertReceiverId || !siteId) {
			return;
		}

		experienceTracker.start({
			name: ExperienceName.REMIX_INSERT,
			id: ExperienceName.REMIX_INSERT,
			attributes: {
				actionKey,
				dynamicUiType,
				contentType: 'image',
				conversationChannelId: conversationId,
			},
		});

		trackAIMAUEvent({
			type: 'actioned',
			actionResult: 'contentFinalized',
			source: 'AIMate',
			invokedFrom: 'remix',
			proactive: false,
			userGenerated: true,
			singleInstrumentationID: agentMessageId,
			pageContext: 'confluence-page-edit',
			attributes: {
				createdContentProduct: 'confluence',
				createdContentType: 'image',
				createdContentMetadata: {
					mediaFileId,
					isLiveDoc,
				},
				actionKey,
				dynamicUiType,
				dynamicUiSubtype: dynamicUiSubtypeFromContext ?? backendDynamicUiSubtype,
				userAction: 'insert',
			},
		});

		try {
			const mediaUrl = await fetchInfographicUrl({
				mediaFileId,
				clientId,
				product: 'confluence',
			});

			getBrowserBridgeAPI().send(EditorInsertContentCommand, {
				senderId: ROVO_CHAT_CLIENT_ID,
				receiverId: getConfluenceEditorClientId(insertReceiverId),
				mode: 'upload-and-insert-media',
				placement: isPageLevelRemix(dynamicUiSource)
					? 'end-of-doc'
					: isUpdate && fg('platform_editor_ai_remix_toolbar')
						? 'replace-selection'
						: 'after-block',
				useStoredSelection: true,
				conversationChannelId: conversationId,
				interactionSource,
				aspectRatio,
				// The insert command records the subtype the backend actually produced,
				// falling back to the user's pick only if the backend returned none.
				remixSubtype: backendDynamicUiSubtype ?? dynamicUiSubtypeFromContext,
				data: {
					type: 'image',
					url: mediaUrl,
					presentation: undefined,
				},
			});

			experienceTracker.succeed({ name: ExperienceName.REMIX_INSERT });
		} catch (error) {
			experienceTracker.fail({
				name: ExperienceName.REMIX_INSERT,
				error: error instanceof Error ? error : new Error('Failed to insert infographic into page'),
			});
			return;
		}
	}, [
		mediaFileId,
		clientId,
		siteId,
		dynamicUiType,
		actionKey,
		experienceTracker,
		conversationId,
		dynamicUiSource,
		trackAIMAUEvent,
		agentMessageId,
		isLiveDoc,
		insertReceiverId,
		dynamicUiSubtypeFromContext,
		backendDynamicUiSubtype,
		isUpdate,
		interactionSource,
		aspectRatio,
	]);

	const handleInsertMauiEmbedIntoPageRef = useRef(handleInsertMauiEmbedIntoPage);
	useEffect(() => {
		handleInsertMauiEmbedIntoPageRef.current = handleInsertMauiEmbedIntoPage;
	}, [handleInsertMauiEmbedIntoPage]);

	const handleInsertInfographicIntoPageRef = useRef(handleInsertInfographicIntoPage);
	useEffect(() => {
		handleInsertInfographicIntoPageRef.current = handleInsertInfographicIntoPage;
	}, [handleInsertInfographicIntoPage]);

	useEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}
		if (isViewMode) {
			return;
		}
		if (hasEarlyInsertedWhiteboardRef.current) {
			return;
		}
		if (contentClassification?.type !== 'whiteboard' || !contentClassification.data?.contentId) {
			return;
		}

		// Insert the temporary whiteboard shell when the first real ACTION_PART arrives.
		// This avoids showing an empty whiteboard during long gaps between classification
		// and the first streamed diagram content. The same localId is later used to replace
		// this temporary URL with the finalized canonical URL, even if native-embeds has
		// transformed the Smart Link into a native whiteboard extension in the meantime.
		insertEarlyStreamingWhiteboard(contentClassification.data.contentId);
	}, [contentClassification, insertEarlyStreamingWhiteboard, isViewMode]);

	useEffect(() => {
		if (isViewMode) {
			return;
		}

		if (
			actionKey &&
			finalMessage &&
			!hasInsertedRef.current &&
			fg('platform_editor_ai_remix_in_editor_loading')
		) {
			hasInsertedRef.current = true;

			const insertContent = async () => {
				if (actionKey === IMAGE_CREATE_KEY) {
					await handleInsertInfographicIntoPageRef.current();
					// for image, the real fieldId will be updated after the image is uploaded via updateRemixImageHistoryFieldId
					return { fieldId: 'fieldId-placeholder' };
				} else {
					const url = await handleInsertMauiEmbedIntoPageRef.current();

					if (hasEarlyInsertedWhiteboardRef.current) {
						// The ephemeral preview sends a final whiteboard bridge command with
						// `isStreamComplete: true`; the whiteboard client uses that final signal
						// for end-of-stream behaviours such as zoom-to-fit. Since the editor flow
						// inserts the whiteboard outside the preview, mirror that signal here once
						// streaming has completed, even when finalization fails and the inserted
						// diagram remains unfinalized.
						sendWhiteboardStreamCompleteCommand({
							data: finalWhiteboardCreateActionData,
							groupId: conversationId,
							singleInstrumentationID: agentMessageId,
						});
					}

					return { url };
				}
			};

			insertContent()
				.then((result) => {
					if (!result) {
						return;
					}
					const { fieldId, url } = result;
					if (!isUpdate) {
						onRemixInsertSuccess?.({
							currentConversationId: conversationId,
							remixMetadata: getRemixMetadata({
								action,
								selectedRemixMetadata: {
									selectedDynamicUiType: dynamicUiType,
									selectedDynamicUiSubtype: dynamicUiSubtypeFromContext ?? backendDynamicUiSubtype,
									isObjectLevelRemix: !dynamicUiSource
										? undefined
										: isPageLevelRemix(dynamicUiSource),
								},
							}),
						});
					}
					if (!fg('platform_editor_ai_remix_toolbar')) {
						return;
					}
					if (hasEarlyInsertedWhiteboardRef.current && url) {
						finishRemixThinkingRef.current?.();
					}
					if (isUpdate) {
						if (conversationId && historyEntryForUpdate?.dynamicUiType) {
							addHistoryStackEntry?.({
								conversationId,
								dynamicUiType: historyEntryForUpdate.dynamicUiType,
								dynamicUiSubtype: dynamicUiSubtypeForUpdate,
								finalMessage,
								previousFieldId: historyEntryForUpdate.fieldId,
								previousUrl: historyEntryForUpdate.url,
								url,
								fieldId,
							});
						}
						// Release the in-flight mark so the bridge processors resume
						// handling BE pushes for this id. Must match the toolbar's
						// tracking key: image fieldId, or MAUI app id from the
						// *pre-update* stack-entry URL.
						const completionId =
							historyEntryForUpdate?.fieldId ?? getMauiIdFromUrl(historyEntryForUpdate?.url);
						if (completionId) {
							onVariantUpdateComplete?.(completionId);
						}
					} else {
						if (conversationId && dynamicUiSource && dynamicUiType) {
							addHistoryEntry?.({
								conversationId,
								dynamicUiSource,
								// History and the variant picker must reflect the subtype the
								// backend produced, falling back to the user's pick if it returned none.
								dynamicUiSubtype: backendDynamicUiSubtype ?? dynamicUiSubtypeFromContext,
								dynamicUiType,
								finalMessage,
								contentClassification:
									contentClassification?.type === 'whiteboard'
										? { ...contentClassification, data: finalWhiteboardCreateActionData }
										: contentClassification,
								url,
								fieldId,
							});
						}
						// Via the host so the flag renders on a durable surface outliving this flow.
						if (isSubtypeMismatch) {
							onSubtypeMismatch?.();
						}
					}
				})
				.finally(() => {
					resetContext();
					setThinkingCompleteRef.current?.(true);
				});
		}
	}, [
		isViewMode,
		action,
		actionKey,
		finalMessage,
		resetContext,
		conversationId,
		dynamicUiType,
		dynamicUiSource,
		dynamicUiSubtypeFromContext,
		backendDynamicUiSubtype,
		isSubtypeMismatch,
		onSubtypeMismatch,
		contentClassification,
		finalWhiteboardCreateActionData,
		agentMessageId,
		addHistoryEntry,
		addHistoryStackEntry,
		onRemixInsertSuccess,
		onVariantUpdateComplete,
		isUpdate,
		historyEntryForUpdate?.dynamicUiType,
		dynamicUiSubtypeForUpdate,
		historyEntryForUpdate,
	]);

	// reset the hasInsertedRef when the final message is cleared
	useEffect(() => {
		if (!finalMessage && fg('platform_editor_ai_remix_in_editor_loading')) {
			hasInsertedRef.current = false;
			hasEarlyInsertedWhiteboardRef.current = false;
			earlyInsertedWhiteboardLocalIdRef.current = undefined;
			earlyInsertedWhiteboardUrlRef.current = undefined;
			// Backstop for abandoned streams: finalMessage (hence remixId) is null here,
			// so the ref is the only id source. No-op if success/failure already settled.
			if (streamingContentIdRef.current) {
				setMauiEmbedFinalizeStatusRef.current?.({
					contentId: streamingContentIdRef.current,
					status: 'needs-finalize',
				});
				streamingContentIdRef.current = undefined;
			}
		}
	}, [finalMessage]);
}
